/**
 * Reports Page - CR-4099
 *
 * Routes between three subviews of the Reports module:
 *   - Builder       (`?view=builder&indicator=N`)
 *   - Detail view   (`?view=detail&report=N`)
 *   - List view     (default)
 *
 * Heavy UI lives in `ReportListView`, `ReportDetailView`, and the
 * Reports v2 dialog mount (`ReportsDialogs`). This file is intentionally
 * thin to keep it under the 500-line cap.
 */

import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useReports,
  ReportDetailView,
  ReportListView,
} from './report';
import { ReportBuilder } from './report/builder';
import { useIsSupplier } from '../hooks/usePortalMode';

export default function Reports() {
  const [qs, setQs] = useSearchParams();
  const navigate = useNavigate();
  const builderIndicatorId = qs.get('indicator');
  const isBuilderView = qs.get('view') === 'builder' && builderIndicatorId;

  if (isBuilderView) {
    const id = parseInt(builderIndicatorId, 10);
    if (Number.isFinite(id)) {
      return (
        <ReportBuilder
          indicatorId={id}
          onClose={() => {
            // Return to detail view of the parent dashboard if known.
            const dashId = qs.get('report');
            if (dashId) {
              setQs({ view: 'detail', report: dashId });
            } else {
              navigate('/reports');
            }
          }}
        />
      );
    }
  }

  return <ReportsRouter />;
}

function ReportsRouter() {
  const navigateRL = useNavigate();
  const handleCustomiseIndicator = (indicatorId: number, dashboardId?: number) => {
    const params = new URLSearchParams();
    params.set('view', 'builder');
    params.set('indicator', String(indicatorId));
    if (dashboardId) params.set('report', String(dashboardId));
    navigateRL(`/reports?${params.toString()}`);
  };

  const state = useReports();
  const isSupplier = useIsSupplier();

  if (state.loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state.viewMode === 'detail' && state.selectedReport) {
    return (
      <ReportDetailView
        selectedReport={state.selectedReport}
        indicators={state.indicators}
        categories={state.categories}
        datasets={state.datasets}
        loading={state.loading}
        loadingIndicators={state.loadingIndicators}
        saving={state.saving}
        isSupplier={isSupplier}
        isAddIndicatorOpen={state.isAddIndicatorOpen}
        setIsAddIndicatorOpen={state.setIsAddIndicatorOpen}
        newIndicator={state.newIndicator}
        setNewIndicator={state.setNewIndicator}
        onCreateIndicator={state.handleCreateIndicator}
        isEditOpen={state.isEditOpen}
        setIsEditOpen={state.setIsEditOpen}
        reportToEdit={state.reportToEdit}
        isDuplicateOpen={state.isDuplicateOpen}
        setIsDuplicateOpen={state.setIsDuplicateOpen}
        reportToDuplicate={state.reportToDuplicate}
        isShareOpen={state.isShareOpen}
        setIsShareOpen={state.setIsShareOpen}
        reportToShare={state.reportToShare}
        isPublishOpen={state.isPublishOpen}
        setIsPublishOpen={state.setIsPublishOpen}
        reportToPublish={state.reportToPublish}
        isRevisionsOpen={state.isRevisionsOpen}
        setIsRevisionsOpen={state.setIsRevisionsOpen}
        reportForRevisions={state.reportForRevisions}
        isDownloadLogsOpen={state.isDownloadLogsOpen}
        setIsDownloadLogsOpen={state.setIsDownloadLogsOpen}
        isFiscalCalendarOpen={state.isFiscalCalendarOpen}
        setIsFiscalCalendarOpen={state.setIsFiscalCalendarOpen}
        isScheduleOpen={state.isScheduleOpen}
        setIsScheduleOpen={state.setIsScheduleOpen}
        isExportOpen={state.isExportOpen}
        setIsExportOpen={state.setIsExportOpen}
        isFilterOpen={state.isFilterOpen}
        setIsFilterOpen={state.setIsFilterOpen}
        isEditIndicatorOpen={state.isEditIndicatorOpen}
        setIsEditIndicatorOpen={state.setIsEditIndicatorOpen}
        indicatorToEdit={state.indicatorToEdit}
        isDeleteReportOpen={state.isDeleteReportOpen}
        setIsDeleteReportOpen={state.setIsDeleteReportOpen}
        reportToDelete={state.reportToDelete}
        openEditReport={state.openEditReport}
        openDuplicateReport={state.openDuplicateReport}
        openShareReport={state.openShareReport}
        openPublishReport={state.openPublishReport}
        openRevisionHistory={state.openRevisionHistory}
        openDownloadLogs={state.openDownloadLogs}
        openFiscalCalendar={state.openFiscalCalendar}
        openScheduleReport={state.openScheduleReport}
        openExportReport={state.openExportReport}
        openFilterDialog={state.openFilterDialog}
        openEditIndicator={state.openEditIndicator}
        setReportToDelete={state.setReportToDelete}
        onBackToList={state.handleBackToList}
        onRefresh={state.handleRefresh}
        onCustomiseIndicator={handleCustomiseIndicator}
        onEditReport={state.handleEditReport}
        onDuplicateReport={state.handleDuplicateReport}
        onShareReport={state.handleShareReport}
        onScheduleReport={state.handleScheduleReport}
        onExportReport={state.handleExportReport}
        onAfterFilterChange={state.handleAfterFilterChange}
        onEditIndicator={state.handleEditIndicator}
        onDeleteIndicator={state.handleDeleteIndicator}
        onOpenIndicatorBuilder={state.handleOpenIndicatorBuilder}
        onDeleteReport={state.handleDeleteReport}
      />
    );
  }

  return (
    <ReportListView
      datasets={state.datasets}
      categories={state.categories}
      reports={state.reports}
      indicators={state.indicators}
      selectedDataset={state.selectedDataset}
      selectedReport={state.selectedReport}
      loading={state.loading}
      saving={state.saving}
      isSupplier={isSupplier}
      isCreateReportOpen={state.isCreateReportOpen}
      setIsCreateReportOpen={state.setIsCreateReportOpen}
      isCreateCategoryOpen={state.isCreateCategoryOpen}
      setIsCreateCategoryOpen={state.setIsCreateCategoryOpen}
      newReport={state.newReport}
      setNewReport={state.setNewReport}
      newCategory={state.newCategory}
      setNewCategory={state.setNewCategory}
      isEditOpen={state.isEditOpen}
      setIsEditOpen={state.setIsEditOpen}
      reportToEdit={state.reportToEdit}
      isDuplicateOpen={state.isDuplicateOpen}
      setIsDuplicateOpen={state.setIsDuplicateOpen}
      reportToDuplicate={state.reportToDuplicate}
      isShareOpen={state.isShareOpen}
      setIsShareOpen={state.setIsShareOpen}
      reportToShare={state.reportToShare}
      isPublishOpen={state.isPublishOpen}
      setIsPublishOpen={state.setIsPublishOpen}
      reportToPublish={state.reportToPublish}
      isRevisionsOpen={state.isRevisionsOpen}
      setIsRevisionsOpen={state.setIsRevisionsOpen}
      reportForRevisions={state.reportForRevisions}
      openRevisionHistory={state.openRevisionHistory}
      isDownloadLogsOpen={state.isDownloadLogsOpen}
      setIsDownloadLogsOpen={state.setIsDownloadLogsOpen}
      openDownloadLogs={state.openDownloadLogs}
      isFiscalCalendarOpen={state.isFiscalCalendarOpen}
      setIsFiscalCalendarOpen={state.setIsFiscalCalendarOpen}
      openFiscalCalendar={state.openFiscalCalendar}
      isScheduleOpen={state.isScheduleOpen}
      setIsScheduleOpen={state.setIsScheduleOpen}
      isExportOpen={state.isExportOpen}
      setIsExportOpen={state.setIsExportOpen}
      isFilterOpen={state.isFilterOpen}
      setIsFilterOpen={state.setIsFilterOpen}
      isEditIndicatorOpen={state.isEditIndicatorOpen}
      setIsEditIndicatorOpen={state.setIsEditIndicatorOpen}
      indicatorToEdit={state.indicatorToEdit}
      isDeleteReportOpen={state.isDeleteReportOpen}
      setIsDeleteReportOpen={state.setIsDeleteReportOpen}
      reportToDelete={state.reportToDelete}
      setSelectedDataset={state.setSelectedDataset}
      setReportToDelete={state.setReportToDelete}
      openEditReport={state.openEditReport}
      openDuplicateReport={state.openDuplicateReport}
      openShareReport={state.openShareReport}
      openPublishReport={state.openPublishReport}
      onViewReport={state.handleViewReport}
      onRefresh={state.handleRefresh}
      onCreateReport={state.handleCreateReport}
      onCreateCategory={state.handleCreateCategory}
      onEditReport={state.handleEditReport}
      onDuplicateReport={state.handleDuplicateReport}
      onShareReport={state.handleShareReport}
      onScheduleReport={state.handleScheduleReport}
      onExportReport={state.handleExportReport}
      onAfterFilterChange={state.handleAfterFilterChange}
      onEditIndicator={state.handleEditIndicator}
      onOpenIndicatorBuilder={state.handleOpenIndicatorBuilder}
      onDeleteReport={state.handleDeleteReport}
    />
  );
}

// Suppress lint for unused module-level helpers retained for other consumers.
void [/* placeholders for future routes */];

// Re-export the dataset icon helper for consistency with prior versions.
export {};
